/**
 * DashboardTableView Component
 *
 * Table view for the dashboard hub. CLONES the visual design of ReportTableView /
 * ClientTableView exactly (UI directive §7 Tables): a rounded-[22px] container on
 * bg-muted/50 with a 0.5px border and shadow-xs, a bg-background header row with
 * text-[13px] font-semibold cells and a 0.5px bottom border, and bg-background
 * rows with a brand hover tint and a ⋮ actions menu. The action menu mirrors the
 * saved-dashboard card's menu (View / Edit / Delete).
 *
 * Columns (the report columns MINUS Format):
 *   Name (pinned, sortable) · Client (hideable+sortable) · Status (hideable+sortable,
 *   DASHBOARD_STATUS_STYLES pill) · Date Range (hideable+sortable, CalendarDots +
 *   formatDashboardRange) · Updated (hideable+sortable) · Created (hideable+sortable)
 *   · Actions (pinned, View / Edit / Delete).
 *
 * Pinned columns (Name + Actions) are never listed in the hideable column array so
 * they can never be hidden. Sort headers + the "eye-off" hide buttons CLONE the
 * ReportTableView pattern exactly.
 */

import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import {
  DotsThreeVertical,
  SortAscending,
  SortDescending,
  PencilSimple,
  ShareNetwork,
  Trash,
  Eye,
  CalendarDots,
} from "@phosphor-icons/react";
import { ClientAvatar } from "../clients/ClientAvatar";
import { TableHeaderIcon } from "../shared/TableHeaderIcon";
import {
  DASHBOARD_STATUS_STYLES,
  formatDashboardRange,
  type DashboardSortOption,
} from "./dashboardColumns";

// ============================================================================
// Types
// ============================================================================

export interface DashboardTableItem {
  id: string;
  name: string;
  // Story 40-7: roll-up dashboards (kind="rollup") carry no bound client, so the
  // LIST endpoint returns clientId/clientName null for them — both nullable here.
  clientId: string | null;
  clientName: string | null;
  status: string;
  dateRangeStart: string | null;
  dateRangeEnd: string | null;
  dateRangePreset: string | null;
  // Story 40-7 roll-up discriminator (absent ⇒ a single-client dashboard).
  kind?: "client" | "rollup";
  rollupScope?: "brand" | "portfolio" | null;
  brandId?: string | null;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

/** Display label for a roll-up dashboard's "client" slot (brand vs portfolio). */
function rollupLabel(item: DashboardTableItem): string {
  return item.rollupScope === "brand" ? "Brand roll-up" : "Portfolio roll-up";
}

interface DashboardTableViewProps {
  dashboards: DashboardTableItem[];
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
  /** The per-row menu's Share — opens the hub-level DashboardShareDialog. */
  onShare?: (dashboard: DashboardTableItem) => void;
  onDelete: (dashboard: DashboardTableItem) => void;
  sortBy?: DashboardSortOption;
  onSortChange?: (sort: DashboardSortOption) => void;
  /** Column ids the user has hidden (from the route, persisted to localStorage). */
  hiddenColumns?: string[];
  /** Retained for call-site compatibility; the Columns toolbar owns visibility. */
  onHideColumn?: (id: string) => void;
}

// ============================================================================
// Helpers
// ============================================================================

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ============================================================================
// Component
// ============================================================================

export function DashboardTableView({
  dashboards,
  onOpen,
  onEdit,
  onShare,
  onDelete,
  sortBy,
  onSortChange,
  hiddenColumns = [],
}: DashboardTableViewProps) {
  const isHidden = (id: string) => hiddenColumns.includes(id);
  return (
    <div
      data-testid="dashboard-table-view"
      className="overflow-hidden rounded-[22px] bg-muted/50 text-foreground shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      <table className="app-page-table w-full text-sm" aria-label="Dashboard list">
        <thead className="relative bg-background">
          <tr>
            {/* Name */}
            <th
              scope="col"
              className="text-left py-2 px-4 text-[13px] font-semibold text-foreground"
            >
              {onSortChange ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                  onClick={() =>
                    onSortChange(sortBy === "name_asc" ? "name_desc" : "name_asc")
                  }
                >
                  <TableHeaderIcon name="dashboard" />
                  Name
                  {(sortBy === "name_asc" || sortBy === "name_desc") &&
                    (sortBy === "name_asc" ? (
                      <SortAscending size={18} />
                    ) : (
                      <SortDescending size={18} />
                    ))}
                </button>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <TableHeaderIcon name="dashboard" />
                  Name
                </span>
              )}
            </th>

            {/* Client */}
            {!isHidden("client") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden md:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="client" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(sortBy === "client_asc" ? "client_desc" : "client_asc")
                      }
                    >
                      Client
                      {(sortBy === "client_asc" || sortBy === "client_desc") &&
                        (sortBy === "client_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Client"
                  )}
                </span>
              </th>
            )}

            {/* Status */}
            {!isHidden("status") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="status" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(sortBy === "status_asc" ? "status_desc" : "status_asc")
                      }
                    >
                      Status
                      {(sortBy === "status_asc" || sortBy === "status_desc") &&
                        (sortBy === "status_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Status"
                  )}
                </span>
              </th>
            )}

            {/* Date Range */}
            {!isHidden("dateRange") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden lg:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="dateRange" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(
                          sortBy === "date_range_asc" ? "date_range_desc" : "date_range_asc"
                        )
                      }
                    >
                      Date Range
                      {(sortBy === "date_range_asc" || sortBy === "date_range_desc") &&
                        (sortBy === "date_range_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Date Range"
                  )}
                </span>
              </th>
            )}

            {/* Updated */}
            {!isHidden("updated") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden sm:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="updated" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(sortBy === "updated_desc" ? "updated_asc" : "updated_desc")
                      }
                    >
                      Updated
                      {(sortBy === "updated_asc" || sortBy === "updated_desc") &&
                        (sortBy === "updated_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Updated"
                  )}
                </span>
              </th>
            )}

            {/* Created */}
            {!isHidden("created") && (
              <th
                scope="col"
                className="group w-px text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden sm:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="created" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(sortBy === "created_desc" ? "created_asc" : "created_desc")
                      }
                    >
                      Created
                      {(sortBy === "created_asc" || sortBy === "created_desc") &&
                        (sortBy === "created_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Created"
                  )}
                </span>
              </th>
            )}

            {/* Actions */}
            <th scope="col" className="py-2 px-4 w-16">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {dashboards.map((dashboard) => {
            const statusStyle = DASHBOARD_STATUS_STYLES[dashboard.status];
            return (
              <tr
                key={dashboard.id}
                data-testid="dashboard-row"
                className="bg-background hover:bg-brand/[0.02] transition-colors cursor-pointer"
                onClick={() => onOpen(dashboard.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen(dashboard.id);
                  }
                }}
                tabIndex={0}
              >
                {/* Name */}
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="font-medium truncate">{dashboard.name}</p>
                  </div>
                </td>

                {/* Client */}
                {!isHidden("client") &&
                  (() => {
                    // Story 40-7: a roll-up row has no bound client — show a
                    // brand/portfolio roll-up label in the client cell. Single-
                    // client rows render byte-identically.
                    const clientLabel =
                      dashboard.kind === "rollup"
                        ? rollupLabel(dashboard)
                        : (dashboard.clientName ?? "");
                    return (
                      <td className="py-3 px-4 text-foreground hidden md:table-cell whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <ClientAvatar
                            name={clientLabel}
                            logoUrl={null}
                            size="sm"
                          />
                          <span className="truncate">{clientLabel}</span>
                        </div>
                      </td>
                    );
                  })()}

                {/* Status */}
                {!isHidden("status") && (
                  <td className="py-3 px-4 whitespace-nowrap">
                    {statusStyle && (
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusStyle.className}`}
                      >
                        {statusStyle.label}
                      </span>
                    )}
                  </td>
                )}

                {/* Date Range */}
                {!isHidden("dateRange") && (
                  <td className="py-3 px-4 text-foreground whitespace-nowrap hidden lg:table-cell">
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDots size={16} />
                      {formatDashboardRange(dashboard)}
                    </span>
                  </td>
                )}

                {/* Updated */}
                {!isHidden("updated") && (
                  <td className="py-3 px-4 text-foreground whitespace-nowrap hidden sm:table-cell">
                    {formatTimestamp(dashboard.updatedAt)}
                  </td>
                )}

                {/* Created */}
                {!isHidden("created") && (
                  <td className="w-px py-3 px-4 text-foreground whitespace-nowrap hidden sm:table-cell">
                    {formatTimestamp(dashboard.createdAt)}
                  </td>
                )}

                {/* Actions */}
                <td
                  className="py-3 px-4 text-right"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="min-h-11 min-w-11 cursor-pointer rounded-lg border border-transparent hover:bg-background hover:border-input transition-all"
                        aria-label={`Actions for ${dashboard.name}`}
                        data-testid={`dashboard-row-menu-${dashboard.id}`}
                      >
                        <DotsThreeVertical size={28} weight="bold" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="rounded-[23px] p-2 shadow-md"
                      style={{ border: "0.5px solid var(--hairline)" }}
                    >
                      <DropdownMenuItem
                        className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                        onClick={() => onOpen(dashboard.id)}
                      >
                        <Eye size={14} className="mr-1" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                        onClick={() => onEdit(dashboard.id)}
                      >
                        <PencilSimple size={14} className="mr-1" />
                        Edit
                      </DropdownMenuItem>
                      {onShare && (
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                          onClick={() => onShare(dashboard)}
                          data-testid={`dashboard-row-share-${dashboard.id}`}
                        >
                          <ShareNetwork size={14} className="mr-1" />
                          Share
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="min-h-11 text-[13px] text-destructive cursor-pointer rounded-lg hover:!bg-destructive/10 focus:!bg-destructive/10 focus:!text-destructive focus:!border-destructive/30"
                        onClick={() => onDelete(dashboard)}
                      >
                        <Trash size={14} className="mr-1" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
